import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Database,
  Download,
  Check,
  Copy,
  Shield,
  Key,
  FileCode2,
  AlertTriangle,
  Info,
} from 'lucide-react';
import api from '../api/client';

// ColumnSpec mirrors the Go struct internal/connector/contract/contract.go.
// Keep JSON tag names in sync: name / type / required / description.
type ColumnSpec = {
  name: string;
  type: string;
  required: boolean;
  description: string;
};

type EntityContract = {
  name: string;
  description: string;
  dedup_key: string;
  required_columns: ColumnSpec[];
};

type EntitiesResponse = {
  drivers: string[];
  entities: EntityContract[];
};

// The Docs page is intentionally self-contained: one API call, no routing
// sub-components. The payload is tiny (a few KB) and the contract changes
// only on deploy, so memoization gives zero benefit.
function useContract() {
  const [data, setData] = useState<EntitiesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api
      .get<EntitiesResponse>('/connectors/contract/entities')
      .then((res) => {
        if (!cancelled) {
          setData(res.data);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(String(err?.message || err));
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { data, error, loading };
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={copy}
      className="absolute right-3 top-3 rounded p-1 text-slate-400 hover:text-white transition-colors"
      title="Copy"
    >
      {copied ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
    </button>
  );
}

function CodeBlock({ code, language = 'sql' }: { code: string; language?: string }) {
  return (
    <div className="relative mt-3 rounded-lg bg-slate-900 dark:bg-slate-950 border border-slate-700">
      <div className="flex items-center justify-between border-b border-slate-700 px-4 py-2">
        <span className="text-xs font-medium text-slate-400">{language.toUpperCase()}</span>
      </div>
      <CopyButton text={code} />
      <pre className="overflow-x-auto p-4 text-sm leading-relaxed text-slate-300">
        <code>{code}</code>
      </pre>
    </div>
  );
}

// Resolve the download URL directly against the public contract endpoint. We
// do NOT use the authenticated axios client because downloads must work even
// if the operator is not logged in yet — the backend endpoint intentionally
// bypasses IAM JWT auth for this exact reason.
function resolveDownloadUrl(driver: string, entity: string): string {
  const apiRoot = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');
  return `${apiRoot}/api/v1/connectors/contract/ddl/${driver}/${entity}.sql`;
}

export function DataSourceContract() {
  const { t } = useTranslation();
  const { data, error, loading } = useContract();

  const [selectedDriver, setSelectedDriver] = useState<string>('mssql');

  // When the payload arrives, default to the first driver so the page
  // renders without the user having to click anything.
  useEffect(() => {
    if (data && data.drivers.length > 0 && !data.drivers.includes(selectedDriver)) {
      setSelectedDriver(data.drivers[0]);
    }
  }, [data, selectedDriver]);

  const grantExample = useMemo(
    () =>
      `-- Minimum permissions the Connector Worker needs (MSSQL)
CREATE LOGIN bs_reporting_ro WITH PASSWORD = '<STRONG-RANDOM-SECRET>';
CREATE USER bs_reporting_ro FOR LOGIN bs_reporting_ro;

-- Grant SELECT on every contract view
GRANT SELECT ON SCHEMA::bs_reporting TO bs_reporting_ro;

-- Recommended: firewall the bs-reporting Connector Worker egress IPs.`,
    [],
  );

  if (loading) {
    return (
      <div className="p-8 text-slate-600 dark:text-slate-300">
        {t('docs.dataSourceContract.loading', 'Loading Data Source Contract…')}
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-8">
        <div className="rounded-lg border border-rose-500/40 bg-rose-50 p-4 text-rose-800 dark:bg-rose-900/20 dark:text-rose-300">
          <AlertTriangle className="mb-2 h-5 w-5" />
          <p className="text-sm font-medium">
            {t('docs.dataSourceContract.loadError', 'Failed to load contract')}
          </p>
          <p className="mt-1 text-xs opacity-80">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 p-6 md:p-8">
      {/* Header */}
      <div className="flex items-start gap-4">
        <div className="rounded-lg bg-accent/10 p-3">
          <Database className="h-6 w-6 text-accent" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">
            {t('docs.dataSourceContract.title', 'Data Source Contract')}
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-slate-600 dark:text-slate-400">
            {t(
              'docs.dataSourceContract.subtitle',
              'Standardized views your PAM database must expose so the Connector Worker can pull data into bs-reporting without custom mappings.',
            )}
          </p>
        </div>
      </div>

      {/* Intro card */}
      <div className="rounded-lg border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-900">
        <div className="flex items-start gap-3">
          <Info className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
          <div className="space-y-2 text-sm text-slate-700 dark:text-slate-300">
            <p>
              {t(
                'docs.dataSourceContract.introStep1',
                'Create each view below in your source database under the [bs_reporting] schema (or any schema you prefer).',
              )}
            </p>
            <p>
              {t(
                'docs.dataSourceContract.introStep2',
                'Grant a read-only user SELECT permission on the views, then configure the Connection from Settings → Data Sources.',
              )}
            </p>
            <p>
              {t(
                'docs.dataSourceContract.introStep3',
                'Every row MUST have a monotonically-increasing id column. bs-reporting uses it as the sync cursor AND the dedup key against the push channel.',
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Driver tabs */}
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-slate-500">
          {t('docs.dataSourceContract.driverLabel', 'Driver:')}
        </span>
        {data.drivers.map((d) => (
          <button
            key={d}
            onClick={() => setSelectedDriver(d)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              d === selectedDriver
                ? 'bg-accent text-white'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300'
            }`}
          >
            {d.toUpperCase()}
          </button>
        ))}
      </div>

      {/* Entities */}
      <div className="space-y-6">
        {data.entities.map((ent) => (
          <section
            key={ent.name}
            className="rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900"
          >
            <header className="flex items-center justify-between border-b border-slate-200 px-5 py-4 dark:border-slate-700">
              <div className="flex items-center gap-3">
                <FileCode2 className="h-5 w-5 text-accent" />
                <div>
                  <h2 className="font-mono text-base font-semibold text-slate-900 dark:text-slate-100">
                    {ent.name}
                  </h2>
                  <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                    {ent.description}
                  </p>
                </div>
              </div>
              <a
                href={resolveDownloadUrl(selectedDriver, ent.name)}
                download={`${ent.name}.sql`}
                className="inline-flex items-center gap-2 rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent/90"
              >
                <Download className="h-4 w-4" />
                {t('docs.dataSourceContract.downloadSQL', 'Download SQL')}
              </a>
            </header>

            <div className="px-5 py-4">
              <div className="mb-3 flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                <Key className="h-3.5 w-3.5" />
                <span>
                  {t('docs.dataSourceContract.dedupKey', 'Dedup key:')}{' '}
                  <code className="rounded bg-slate-100 px-1 py-0.5 font-mono text-xs text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                    {ent.dedup_key}
                  </code>
                </span>
              </div>

              <div className="overflow-x-auto rounded-md border border-slate-200 dark:border-slate-700">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-50 dark:bg-slate-800/80">
                    <tr>
                      <th className="px-4 py-2 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
                        {t('docs.dataSourceContract.colColumn', 'Column')}
                      </th>
                      <th className="px-4 py-2 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
                        {t('docs.dataSourceContract.colType', 'Type')}
                      </th>
                      <th className="w-16 px-4 py-2 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
                        {t('docs.dataSourceContract.colRequired', 'Req.')}
                      </th>
                      <th className="px-4 py-2 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
                        {t('docs.dataSourceContract.colDescription', 'Description')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                    {ent.required_columns.map((c) => (
                      <tr
                        key={c.name}
                        className="bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800/50"
                      >
                        <td className="px-4 py-2 font-mono text-xs text-accent dark:text-accent-300">
                          {c.name}
                        </td>
                        <td className="px-4 py-2 font-mono text-xs text-amber-600 dark:text-amber-400 whitespace-nowrap">
                          {c.type}
                        </td>
                        <td className="px-4 py-2 text-xs">
                          {c.required ? (
                            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                              <Check className="h-3 w-3" />
                            </span>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-xs text-slate-600 dark:text-slate-400">
                          {c.description}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        ))}
      </div>

      {/* Grant example */}
      <section className="rounded-lg border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-900">
        <div className="flex items-start gap-3">
          <Shield className="mt-0.5 h-5 w-5 text-accent" />
          <div className="flex-1">
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
              {t('docs.dataSourceContract.grantsTitle', 'Minimum permissions')}
            </h2>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
              {t(
                'docs.dataSourceContract.grantsSubtitle',
                'Create a dedicated read-only user for the Connector Worker — never reuse an application account.',
              )}
            </p>
            <CodeBlock code={grantExample} />
          </div>
        </div>
      </section>
    </div>
  );
}

export default DataSourceContract;
